import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import {
  readConsent, saveConsent, applyConsent, gpcEnabled, OPEN_PREFS_EVENT,
} from "@/lib/cookieConsent";

function Toggle({ on, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus-ring ${
        on ? "bg-pnr-green" : "bg-[#3f3f46]"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function Category({ title, desc, on, disabled, badge, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-surface-border py-3 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white">{title}</span>
          {badge && <span className="rounded bg-surface-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-meta">{badge}</span>}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-meta">{desc}</p>
      </div>
      <Toggle on={on} disabled={disabled} onChange={onChange} label={title} />
    </div>
  );
}

export default function CookieConsent() {
  const [consent, setConsent] = useState(() => readConsent());
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const gpc = gpcEnabled();
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  // On mount: apply whatever is stored (opt PostHog in/out), and show the banner if no choice yet.
  useEffect(() => {
    const stored = readConsent();
    applyConsent(stored);
    if (!stored) setBannerOpen(true);
    else { setAnalytics(!!stored.analytics); setAdvertising(!!stored.advertising); }
    const openPrefs = () => {
      const s = readConsent();
      setAnalytics(!!(s && s.analytics));
      setAdvertising(!!(s && s.advertising));
      setPrefsOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, openPrefs);
  }, []);

  const persist = (choice) => {
    const rec = saveConsent(choice);
    setConsent(rec);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const acceptAll = () => persist({ analytics: true, advertising: true });
  const rejectAll = () => persist({ analytics: false, advertising: false });
  const saveCustom = () => persist({ analytics, advertising });

  if (!bannerOpen && !prefsOpen) return null;

  return (
    <>
      {/* First-visit banner */}
      {bannerOpen && !prefsOpen && (
        <div className="fixed inset-x-0 bottom-0 z-[9999] border-t border-surface-border bg-surface-2/95 pb-[env(safe-area-inset-bottom)] shadow-[var(--elev-4)] backdrop-blur-xl" data-testid="cookie-banner">
          <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <Icon name="shield-check" size={20} className="mt-0.5 shrink-0 text-pnr-green" />
              <p className="text-xs leading-relaxed text-meta">
                We use necessary cookies to run PrymeBet and keep you signed in. With your consent we also use optional{" "}
                <b className="text-white">analytics</b> to improve the product. Optional cookies stay off until you accept.{" "}
                {gpc && <span className="text-pnr-green">A Global Privacy Control signal was detected and is honored.</span>}{" "}
                See our <Link to="/cookies" className="text-pnr-green underline underline-offset-2 hover:text-white focus-ring">Cookie &amp; Tracking Notice</Link>.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button data-testid="cookie-customize" onClick={() => setPrefsOpen(true)}
                className="rounded-full border border-surface-border bg-surface-5 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring">Customize</button>
              <button data-testid="cookie-reject" onClick={rejectAll}
                className="rounded-full border border-surface-border bg-surface-5 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring">Reject optional</button>
              <button data-testid="cookie-accept" onClick={acceptAll}
                className="rounded-full bg-pnr-green px-4 py-2 text-xs font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring">Accept optional</button>
            </div>
          </div>
        </div>
      )}

      {/* Customize / manage preferences modal */}
      {prefsOpen && (
        <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-[hsl(var(--overlay-scrim))] p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="cookie-prefs"
          onClick={(e) => { if (e.target === e.currentTarget) setPrefsOpen(false); }}>
          <div className="w-full max-w-lg rounded-t-2xl border border-surface-border bg-surface-2 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-[var(--elev-4)] sm:rounded-2xl sm:pb-5">
            <div className="mx-auto mb-3 h-1 w-9 rounded-full bg-surface-border sm:hidden" />
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-black tracking-tight text-white">Cookie preferences</h2>
              <button aria-label="Close" onClick={() => setPrefsOpen(false)} className="flex h-11 w-11 items-center justify-center text-[#71717a] transition-colors hover:text-white focus-ring"><Icon name="x" size={18} /></button>
            </div>
            <p className="mb-2 text-xs leading-relaxed text-meta">
              Choose which optional cookies PrymeBet may use. Necessary and functional cookies are always on because the
              Platform can't work without them. You can change these choices any time from the Cookie Preferences link.
            </p>
            {gpc && (
              <div className="mb-3 rounded-lg border border-pnr-green/30 bg-pnr-green/10 px-3 py-2 text-xs text-pnr-green" data-testid="cookie-gpc-note">
                A Global Privacy Control signal was detected. Advertising/sharing stays off regardless of the toggles below.
              </div>
            )}
            <div className="rounded-xl border border-surface-border bg-surface-3 px-4">
              <Category title="Necessary" badge="Always on" disabled on
                desc="Sign-in session, authentication, and security. Required to operate the Platform." onChange={() => {}} />
              <Category title="Functional" badge="Always on" disabled on
                desc="First-party preferences such as odds format and notification settings." onChange={() => {}} />
              <Category title="Analytics" on={analytics} onChange={setAnalytics}
                desc="Product analytics to measure usage and errors. Off until you accept." />
              <Category title="Advertising" on={advertising} onChange={setAdvertising}
                desc="No advertising or cross-site tracking tools are currently in use. This stays off; if any are ever added they will be listed and gated by this choice." />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button data-testid="cookie-prefs-reject" onClick={rejectAll}
                className="rounded-full border border-surface-border bg-surface-5 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-surface-4 hover:border-border-strong focus-ring">Reject optional</button>
              <button data-testid="cookie-prefs-save" onClick={saveCustom}
                className="rounded-full bg-pnr-green px-4 py-2 text-xs font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring">Save choices</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
